import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Flame,
  Calendar,
  Trophy,
  Clock,
  Play,
  CheckCircle,
  Star,
  Share2,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import BadgeArt from '../components/BadgeArt';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STREAK_MILESTONES = [7, 30, 100, 365];

// Tier metadata — mirrors CLASSIC_TIERS in server.py
const TIERS = [
  { key: 'easy',   label: 'Easy',   sub: 'Levels 2–3',  accent: '#6E9B5A' },
  { key: 'medium', label: 'Medium', sub: 'Levels 4–5',  accent: '#8B5A3C' },
  { key: 'hard',   label: 'Hard',   sub: 'Level 6',     accent: '#C4573E' },
];

function TierCard({ tier, puzzle, completed, onStart }) {
  return (
    <Card
      className="bg-[var(--background-surface)] border-[var(--border-subtle)] flex flex-col"
      data-testid={`tier-card-${tier.key}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: tier.accent }}
            aria-hidden="true"
          />
          <CardTitle className="text-[var(--text-primary)] font-heading text-lg">
            {tier.label}
          </CardTitle>
        </div>
        <p className="text-xs text-[var(--text-secondary)]">{tier.sub}</p>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col justify-between">
        {puzzle && (
          <>
            <div className="space-y-2 mb-4 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">Technique</span>
                <span className="text-[var(--text-primary)] font-medium truncate ml-2 text-right">
                  {puzzle.difficulty_name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">Solvers</span>
                <span className="text-[var(--text-primary)] font-mono">
                  {puzzle.participants}
                </span>
              </div>
            </div>
            {completed ? (
              <div
                className="flex items-center justify-center gap-2 p-3 rounded bg-[var(--success)]/10 border border-[var(--success)]/30"
                data-testid={`tier-completed-${tier.key}`}
              >
                <CheckCircle className="w-4 h-4 text-[var(--success)]" />
                <span className="text-[var(--success)] text-sm font-medium">Completed</span>
              </div>
            ) : (
              <Button
                onClick={() => onStart(tier.key, puzzle)}
                className="w-full text-white font-semibold"
                style={{ backgroundColor: tier.accent }}
                data-testid={`start-tier-${tier.key}-btn`}
              >
                <Play className="w-4 h-4 mr-2" />
                Play
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function StreakCard({ user, streakInfo, nextMilestone, milestoneProgress, onShare }) {
  return (
    <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]" data-testid="streak-card">
      <CardHeader>
        <CardTitle className="text-[var(--text-primary)] font-heading flex items-center gap-2">
          <Flame className="w-5 h-5 text-[var(--warning)]" />
          Your Streak
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center mb-4">
          <div className="text-5xl font-bold text-[var(--text-primary)] font-mono mb-1">
            {streakInfo.streak}
          </div>
          <div className="text-[var(--text-secondary)]">
            {streakInfo.streak === 1 ? 'day' : 'days'}
          </div>
        </div>

        {streakInfo.at_risk && !streakInfo.completed_today && (
          <div className="flex items-center gap-2 p-3 rounded bg-[var(--warning)]/10 border border-[var(--warning)]/25 mb-4">
            <Flame className="w-4 h-4 text-[var(--warning)]" />
            <span className="text-sm text-[var(--warning)]">
              Complete any tier today to keep your streak!
            </span>
          </div>
        )}

        {nextMilestone && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[var(--text-secondary)]">Next milestone</span>
              <span className="text-[var(--text-primary)] font-mono flex items-center gap-1">
                <Star className="w-3 h-3 text-[var(--warning)]" />
                {nextMilestone} days
              </span>
            </div>
            <Progress value={milestoneProgress} className="h-2" />
          </div>
        )}

        {!user && (
          <p className="text-sm text-[var(--text-disabled)] text-center mt-4">
            Sign in to track your streak
          </p>
        )}

        {user && streakInfo.streak > 0 && (
          <Button
            onClick={onShare}
            variant="outline"
            className="w-full mt-4 border-[var(--border-medium)] text-[var(--text-primary)] hover:bg-[var(--hover-bg)]"
            data-testid="share-streak-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share Streak
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function LeaderboardCard({ leaderboard, formatTime, activeTier, onTierChange }) {
  return (
    <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]" data-testid="leaderboard-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-[var(--text-primary)] font-heading flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[var(--warning)]" />
          Today&apos;s Top Solvers
        </CardTitle>
        <div className="flex gap-1 p-1 rounded-md bg-[var(--background-elevated)]">
          {TIERS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => onTierChange(t.key)}
              className={`text-xs px-2.5 py-1 rounded transition-colors ${
                activeTier === t.key
                  ? 'text-white'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
              style={activeTier === t.key ? { backgroundColor: t.accent } : {}}
              data-testid={`lb-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {leaderboard.length === 0 ? (
          <p className="text-center text-[var(--text-secondary)] py-6 text-sm">
            No solvers yet for this tier — be the first!
          </p>
        ) : (
          <ol className="space-y-2">
            {leaderboard.map((entry, idx) => (
              <li
                key={`${entry.user_id}-${idx}`}
                className="flex items-center justify-between p-2 rounded bg-[var(--background-elevated)]"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-6 text-center text-[var(--text-secondary)] font-mono text-sm">
                    {idx + 1}
                  </span>
                  {entry.top_badge && <BadgeArt badge={entry.top_badge} size={20} />}
                  <span className="text-[var(--text-primary)] truncate">
                    {entry.user_name || 'Anonymous'}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-[var(--text-primary)] font-mono text-sm shrink-0">
                  <Clock className="w-3.5 h-3.5 text-[var(--text-secondary)]" />
                  {formatTime(entry.time_elapsed)}
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function MilestonesSection({ currentStreak }) {
  return (
    <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] mt-6">
      <CardHeader>
        <CardTitle className="text-[var(--text-primary)] font-heading flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[var(--accent-primary)]" />
          Streak Milestones
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {STREAK_MILESTONES.map((m) => {
            const reached = currentStreak >= m;
            return (
              <div
                key={m}
                className={`p-3 rounded text-center border ${
                  reached
                    ? 'bg-[var(--warning)]/10 border-[var(--warning)]/40'
                    : 'bg-[var(--background-elevated)] border-[var(--border-subtle)]'
                }`}
              >
                <div className={`text-xl font-bold font-mono ${
                  reached ? 'text-[var(--warning)]' : 'text-[var(--text-primary)]'
                }`}>
                  {m}
                </div>
                <div className="text-xs text-[var(--text-secondary)]">days</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// Main
export default function DailyChallengePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [puzzles, setPuzzles] = useState({ easy: null, medium: null, hard: null });
  const [streakInfo, setStreakInfo] = useState({
    streak: 0,
    completed_today: false,
    completed_tiers: [],
  });
  const [lbTier, setLbTier] = useState('medium');
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchClassic = useCallback(async () => {
    try {
      const [classicRes, lbRes] = await Promise.all([
        axios.get(`${API_URL}/api/classic-sudoku`, { withCredentials: true }),
        axios.get(`${API_URL}/api/daily-challenge/leaderboard?tier=${lbTier}`, {
          withCredentials: true,
        }),
      ]);
      setPuzzles(classicRes.data.puzzles || {});
      setLeaderboard(lbRes.data || []);

      if (user) {
        const streakRes = await axios.get(`${API_URL}/api/daily-challenge/streak`, {
          withCredentials: true,
        });
        setStreakInfo({ completed_tiers: [], ...streakRes.data });
      }
    } catch (err) {
      toast.error('Failed to load Classic Sudoku');
    } finally {
      setLoading(false);
    }
  }, [user, lbTier]);

  useEffect(() => {
    fetchClassic();
  }, [fetchClassic]);

  const startTier = useCallback((tierKey, puzzle) => {
    if (!user) {
      toast.error('Please sign in to play Classic Sudoku');
      navigate('/login');
      return;
    }
    if ((streakInfo.completed_tiers || []).includes(tierKey)) {
      toast.info(`You've already completed today's ${tierKey} puzzle`);
      return;
    }
    navigate(`/daily/${puzzle.id}?tier=${tierKey}`);
  }, [user, streakInfo.completed_tiers, navigate]);

  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const nextMilestone = useMemo(() => {
    for (const m of STREAK_MILESTONES) {
      if (streakInfo.streak < m) return m;
    }
    return null;
  }, [streakInfo.streak]);

  const milestoneProgress = useMemo(() => {
    if (!nextMilestone) return 100;
    const idx = STREAK_MILESTONES.indexOf(nextMilestone) - 1;
    const prev = idx >= 0 ? STREAK_MILESTONES[idx] : 0;
    return ((streakInfo.streak - prev) / (nextMilestone - prev)) * 100;
  }, [streakInfo.streak, nextMilestone]);

  const handleShareStreak = useCallback(() => {
    let emoji = '🧠';
    if (streakInfo.streak >= 30) emoji = '🔥';
    else if (streakInfo.streak >= 7) emoji = '⚡';
    const text = `${emoji} I'm on a ${streakInfo.streak} day Classic Sudoku streak at Sudoku Academy!\n\nJoin me for daily easy/medium/hard puzzles.\n\n#SudokuAcademy #ClassicSudoku`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  }, [streakInfo.streak]);

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-[var(--accent-primary)]/30 border-t-[var(--accent-primary)] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const completedTiers = streakInfo.completed_tiers || [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
            style={{
              backgroundColor: 'rgba(139, 90, 60, 0.10)',
              border: '1px solid rgba(139, 90, 60, 0.28)',
            }}
          >
            <Calendar className="w-4 h-4 text-[var(--accent-primary)]" />
            <span className="text-sm text-[var(--accent-primary)] font-medium">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-2">
            Classic Sudoku
          </h1>
          <p className="text-[var(--text-secondary)]">
            Three fresh puzzles every day — Easy, Medium, and Hard. Complete any one to keep your streak.
          </p>
        </div>

        {/* 3 tier cards */}
        <div className="grid gap-4 sm:grid-cols-3 mb-6" data-testid="classic-tier-grid">
          {TIERS.map((t) => (
            <TierCard
              key={t.key}
              tier={t}
              puzzle={puzzles[t.key]}
              completed={completedTiers.includes(t.key)}
              onStart={startTier}
            />
          ))}
        </div>

        {/* Streak + Leaderboard */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <StreakCard
            user={user}
            streakInfo={streakInfo}
            nextMilestone={nextMilestone}
            milestoneProgress={milestoneProgress}
            onShare={handleShareStreak}
          />
          <LeaderboardCard
            leaderboard={leaderboard}
            formatTime={formatTime}
            activeTier={lbTier}
            onTierChange={setLbTier}
          />
        </div>

        <MilestonesSection currentStreak={streakInfo.streak} />
      </main>
    </div>
  );
}
